import React, { useEffect, useMemo, useState, useContext } from "react";
import { PrivilegeContext } from "../../../context/privilegeContext";
import "./VehicleDetailTable.css";
import { Loader } from "@vds/loaders";
import { getVehicleDetailInformation } from "../../services/C4S Decommission - Replacement/vehicleManagement";
import WarningIcon from "../../assets/icons/warning.png";

// Main categories constant
const MAIN_CATEGORIES = ["Core - Main"];

// Helper function to check main category
const isMainCategory = (category) => MAIN_CATEGORIES.includes(category);

// Helper function to sort vehicles
const sortVehicles = (vehicles) => {
  return vehicles.slice().sort((a, b) => {
    const aHasMain = Array.isArray(a.items) && a.items.some((item) => isMainCategory(item.category));
    const bHasMain = Array.isArray(b.items) && b.items.some((item) => isMainCategory(item.category));
    if (aHasMain && !bHasMain) return -1;
    if (!aHasMain && bHasMain) return 1;
    return 0;
  });
};

// Helper function to sort items
const sortItems = (items) => {
  // Priority: Core - Main first (if present), otherwise Companion - Main first, then everything else.
  const CORE = "Core - Main";
  const COMPANION = "Companion - Main";

  const hasCore = items?.some((it) => it?.category === CORE);

  const priorityOf = (category) => {
    if (hasCore) return category === CORE ? 0 : 1; // Core first if exists
    return category === COMPANION ? 0 : 1; // else Companion first if exists
  };

  return items.slice().sort((a, b) => {
    const aP = priorityOf(a?.category);
    const bP = priorityOf(b?.category);
    if (aP !== bP) return aP - bP;
    return 0;
  });
};

const VehicleDetailTable = ({ row }) => {
  const [data, setData] = useState(null);
  const [isLoading, setLoader] = useState(false);
  const { cspInfo } = useContext(PrivilegeContext);
  const cspData = cspInfo.data.data;

  const columns = useMemo(
    () => [
      { key: "subscription", label: "Subscription" },
      { key: "category", label: "Category" },
      { key: "hardware", label: "Hardware" },
      { key: "subStatus", label: "Sub Status" },
      { key: "MaterialsubStatus", label: "Sub Status" },
      { key: "appointmentAddress", label: "Appointment Address" },
    ],
    []
  );

  useEffect(() => {
    setLoader(true);
    const crmRefId = cspData?.CSP?.[0]?.CRM_Ref_ID;
    if (row?.bundleId && row?.id && crmRefId) {
      getVehicleDetailInformation(row.bundleId, crmRefId, row.id)
        .then((result) => {
          setData(result?.data?.data);
          setLoader(false);
        })
        .catch(() => {
          setData(null);
          setLoader(false);
        });
    }
  }, [row, cspData]);

  // Memoize sorted vehicles
  const sortedVehicles = useMemo(() => {
    if (!data?.vehicles) return [];
    return sortVehicles(data.vehicles);
  }, [data?.vehicles]);

  return (
    <div className="vehicle-detail-table-container" style={{ minHeight: 120, display: "flex", flexDirection: "column", justifyContent: "center" }}>
      {isLoading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
          <Loader active fullscreen={false} />
        </div>
      ) : !data || !Array.isArray(data.vehicles) || data.vehicles.length === 0 ? (
        <div className="vehicle-detail-table-container" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 120 }}>
          No data available
        </div>
      ) : (
        <div className="vehicle-detail-table-container">
          <table className="vehicle-detail-table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th scope="col" key={col.key} className="vehicle-detail-th">
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedVehicles.map((sub) => {
                if (!sub.items || !Array.isArray(sub.items) || sub.items.length === 0) {
                  return null;
                }
                const sortedItems = sortItems(sub.items);
                return (
                  <React.Fragment key={sub.id}>
                    {sortedItems.map((item, index) => (
                      <tr
                        key={`${sub.id}-${index}`}
                        className="vehicle-detail-row"
                        style={{
                          borderBottom: index === sortedItems.length - 1 ? "1px solid #eee" : "none",
                        }}
                      >
                        {index === 0 && (
                          <td rowSpan={sortedItems.length} className="vehicle-detail-td vehicle-detail-td-sub">
                            <div>
                              {sub.name && sub.name.trim() !== "" ? sub.name : "--"} ({sub.mmid && sub.mmid.trim() !== "" ? sub.mmid : "--"})
                            </div>
                            <div>
                              <span className="vehicle-detail-metadata-label">TECH REF ID:</span>
                              <span className="vehicle-detail-metadata-value">{sub.techRef && sub.techRef.trim() !== "" ? sub.techRef : "--"}</span>
                            </div>
                          </td>
                        )}
                        <td className="vehicle-detail-td">{item.category && item.category.trim() !== "" ? item.category : "--"}</td>
                        <td className="vehicle-detail-td">
                          <div>{item.hardware && item.hardware.trim() !== "" ? item.hardware : "--"}</div>
                          <div style={{ color: "#666", fontWeight: "400", fontSize: "12px" }}>{item.hardwareId}</div>
                        </td>
                        <td className="vehicle-detail-td">
                          <span className={item.status ? "vehicle-detail-badge" : ""}>
                            {item.status && item.status.trim() !== "" ? item.status : "--"}
                          </span>
                          <div style={{ fontSize: "11px", marginTop: "4px" }}>
                            {item.dateLabel == null ? "--" : item.dateLabel}: {item.date && item.date.trim() !== "" ? item.date : "--"}
                          </div>
                        </td>
                        {index === 0 && (
                          <>
                            <td rowSpan={sortedItems.length} className="vehicle-detail-td">
                              <span className={sub.appointmentStatus ? "vehicle-detail-grey-badge" : ""}>
                                {sub.appointmentStatus && sub.appointmentStatus.trim() !== "" ? sub.appointmentStatus : "--"}
                              </span>
                              {sub.qaVisitNeeded ? (
                                <div style={{ color: "#FF8027", gap: "4px" }}>
                                  <img src={WarningIcon} alt="warning-icon" width={12} height={12} />
                                  <span style={{ paddingLeft: "4px" }}>Needs QA</span>
                                </div>
                              ) : (
                                <></>
                              )}
                            </td>
                            <td rowSpan={sortedItems.length} className="vehicle-detail-td">
                              <div style={{ color: "black" }}>
                                {sub.appointmentAddress && sub.appointmentAddress.trim() !== "" ? sub.appointmentAddress : "--"}
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          <div className="vehicle-detail-table-footer">
            <button className="vehicle-history-btn">View History</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleDetailTable;
